import {
  V2InputType,
  V2ParameterSection,
  V2ValidationType,
} from "@/packaging-v2/lib/schema"
import {
  V2ItemFormData,
  V2ParamFormData,
  V2SectionFormData,
} from "@/store/packaging-v2"

// ============================================
// Validation Error Types
// ============================================

export interface ValidationError {
  field: string
  message: string
  paramId?: string
  windowIndex?: number
}

export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
}

// ============================================
// Item Validation
// ============================================

/**
 * Validate item header fields (product, SKU, market type, etc.)
 */
export function validateItemHeader(item: V2ItemFormData): ValidationResult {
  const errors: ValidationError[] = []

  if (!item.productId) {
    errors.push({
      field: "productId",
      message: "Product is required",
    })
  }

  if (!item.skuId) {
    errors.push({
      field: "skuId",
      message: "SKU is required",
    })
  }


  return {
    isValid: errors.length === 0,
    errors,
  }
}

// ============================================
// Section Validation
// ============================================

/**
 * Validate a section's parameters
 */
export function validateSection(section: V2SectionFormData): ValidationResult {
  const errors: ValidationError[] = []

  for (const param of section.parameters) {
    const paramErrors = validateParameter(param)
    errors.push(...paramErrors.errors)
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validate a single parameter
 */
export function validateParameter(param: V2ParamFormData): ValidationResult {
  const errors: ValidationError[] = []

  // Check required field
  if (param.required) {
    if (param.frequencyMinutes && param.windows) {
      // For frequency-based params, validate window readings
      const windowErrors = validateWindowReadings(param)
      errors.push(...windowErrors.errors)
    } else if (!param.value && param.value !== "0") {
      errors.push({
        field: param.name,
        message: `${param.name} is required`,
        paramId: param.id,
      })
    }
  }

  // Skip further validation if no value
  if (!param.value && !param.windows?.some((w) => w.value)) {
    return { isValid: errors.length === 0, errors }
  }

  // Validate based on validation type
  if (param.validationType) {
    switch (param.validationType) {
      case V2ValidationType.LIMIT:
        const limitError = validateLimit(param)
        if (limitError) {
          errors.push(limitError)
        }
        break

      case V2ValidationType.CHOICE:
        const choiceError = validateChoice(param)
        if (choiceError) {
          errors.push(choiceError)
        }
        break

      case V2ValidationType.FLAG:
        // Flag validation - typically boolean
        break

      case V2ValidationType.NONE:
        // No validation required
        break
    }
  }

  // Validate input type
  const inputError = validateInputType(param)
  if (inputError) {
    errors.push(inputError)
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validate window readings for frequency-based parameters
 */
export function validateWindowReadings(
  param: V2ParamFormData
): ValidationResult {
  const errors: ValidationError[] = []

  if (!param.windows || param.windows.length === 0) {
    return { isValid: true, errors: [] }
  }

  for (let i = 0; i < param.windows.length; i++) {
    const window = param.windows[i]

    // Validate window value if present
    if (window.value) {
      // For CHOICE validation
      if (
        param.validationType === V2ValidationType.CHOICE &&
        param.possibleValues
      ) {
        if (!param.possibleValues.includes(window.value)) {
          errors.push({
            field: param.name,
            message: `Invalid value for window ${i + 1}`,
            paramId: param.id,
            windowIndex: i,
          })
        }
      }

      // For LIMIT validation
      if (param.validationType === V2ValidationType.LIMIT) {
        const numValue = parseFloat(window.value)
        if (!isNaN(numValue)) {
          if (param.minLimit !== undefined && numValue < param.minLimit) {
            errors.push({
              field: param.name,
              message: `Value below minimum (${param.minLimit}) in window ${i + 1}`,
              paramId: param.id,
              windowIndex: i,
            })
          }
          if (param.maxLimit !== undefined && numValue > param.maxLimit) {
            errors.push({
              field: param.name,
              message: `Value above maximum (${param.maxLimit}) in window ${i + 1}`,
              paramId: param.id,
              windowIndex: i,
            })
          }
        }
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validate limit (min/max) for numeric values
 */
function validateLimit(param: V2ParamFormData): ValidationError | null {
  if (!param.value) {
    return null
  }

  const numValue = parseFloat(param.value)
  if (isNaN(numValue)) {
    return {
      field: param.name,
      message: `${param.name} must be a number`,
      paramId: param.id,
    }
  }

  if (param.minLimit !== undefined && numValue < param.minLimit) {
    return {
      field: param.name,
      message: `${param.name} must be at least ${param.minLimit}${param.unit ? ` ${param.unit}` : ""}`,
      paramId: param.id,
    }
  }

  if (param.maxLimit !== undefined && numValue > param.maxLimit) {
    return {
      field: param.name,
      message: `${param.name} must be at most ${param.maxLimit}${param.unit ? ` ${param.unit}` : ""}`,
      paramId: param.id,
    }
  }

  return null
}

/**
 * Validate choice (value must be in possibleValues)
 */
function validateChoice(param: V2ParamFormData): ValidationError | null {
  if (!param.value || !param.possibleValues) {
    return null
  }

  if (!param.possibleValues.includes(param.value)) {
    return {
      field: param.name,
      message: `${param.name} must be one of: ${param.possibleValues.join(", ")}`,
      paramId: param.id,
    }
  }

  // Check against correct value if specified
  if (param.correctValue && param.value !== param.correctValue) {
    // This is a warning, not necessarily an error
    // Could be used for UI indication
  }

  return null
}

/**
 * Validate input type (ensure value matches expected type)
 */
function validateInputType(param: V2ParamFormData): ValidationError | null {
  if (!param.value) {
    return null
  }

  switch (param.inputType) {
    case V2InputType.VALUE_NUMBER:
      if (isNaN(parseFloat(param.value))) {
        return {
          field: param.name,
          message: `${param.name} must be a valid number`,
          paramId: param.id,
        }
      }
      break

    case V2InputType.DATE:
      const date = new Date(param.value)
      if (isNaN(date.getTime())) {
        return {
          field: param.name,
          message: `${param.name} must be a valid date`,
          paramId: param.id,
        }
      }
      break

    case V2InputType.DROPDOWN:
    case V2InputType.RADIO:
      if (param.possibleValues && !param.possibleValues.includes(param.value)) {
        return {
          field: param.name,
          message: `${param.name} must be a valid option`,
          paramId: param.id,
        }
      }
      break
  }

  return null
}

// ============================================
// Section-Specific Validators
// ============================================

/**
 * Validate Information Fields section
 */
export function validateInformationFields(
  section: V2SectionFormData,
  item: V2ItemFormData
): ValidationResult {
  const errors: ValidationError[] = []

  // Combine item header validation with section params
  const headerResult = validateItemHeader(item)
  errors.push(...headerResult.errors)

  // Validate section parameters
  const sectionResult = validateSection(section)
  errors.push(...sectionResult.errors)

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validate Labelling Compliance section
 */
export function validateLabellingCompliance(
  section: V2SectionFormData
): ValidationResult {
  return validateSection(section)
}

/**
 * Validate Machine Check section
 */
export function validateMachineCheck(
  section: V2SectionFormData
): ValidationResult {
  // Machine check often has frequency-based params
  return validateSection(section)
}

/**
 * Validate Primary Packaging section
 */
export function validatePrimaryPackaging(
  section: V2SectionFormData
): ValidationResult {
  return validateSection(section)
}

/**
 * Validate Secondary Packaging section
 */
export function validateSecondaryPackaging(
  section: V2SectionFormData
): ValidationResult {
  return validateSection(section)
}

/**
 * Get validator for a specific section type
 */
export function getSectionValidator(
  sectionType: V2ParameterSection
): (section: V2SectionFormData, item?: V2ItemFormData) => ValidationResult {
  switch (sectionType) {
    case V2ParameterSection.INFORMATION_FIELDS:
      return (section, item) =>
        item
          ? validateInformationFields(section, item)
          : validateSection(section)
    case V2ParameterSection.LABELLING_COMPLIANCE:
      return validateLabellingCompliance
    case V2ParameterSection.MACHINE_CHECK:
      return validateMachineCheck
    case V2ParameterSection.PRIMARY_PACKAGING:
      return validatePrimaryPackaging
    case V2ParameterSection.SECONDARY_PACKAGING:
      return validateSecondaryPackaging
    default:
      return validateSection
  }
}

// ============================================
// Full Item Validation
// ============================================

/**
 * Validate an entire item (all sections)
 */
export function validateItem(item: V2ItemFormData): ValidationResult {
  const errors: ValidationError[] = []

  // Validate header
  const headerResult = validateItemHeader(item)
  errors.push(...headerResult.errors)

  // Validate each section
  for (const section of item.sections) {
    const validator = getSectionValidator(section.type)
    const sectionResult = validator(section, item)
    errors.push(...sectionResult.errors)
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Check if value is within limits (for UI indication)
 */
export function isValueWithinLimits(
  value: string,
  minLimit?: number,
  maxLimit?: number
): "valid" | "below" | "above" | "invalid" {
  const numValue = parseFloat(value)

  if (isNaN(numValue)) {
    return "invalid"
  }

  if (minLimit !== undefined && numValue < minLimit) {
    return "below"
  }

  if (maxLimit !== undefined && numValue > maxLimit) {
    return "above"
  }

  return "valid"
}

/**
 * Check if choice value is correct (for UI indication)
 */
export function isChoiceCorrect(
  value: string,
  correctValue?: string
): boolean | null {
  if (!correctValue) {
    return null // No correct value defined
  }

  return value === correctValue
}
